/**
 * Date Formatting Utilities
 *
 * Locale-aware date/time formatting helpers.
 * All exported functions accept a locale parameter (default 'de').
 * IMPORTANT: Always use explicit timeZone to prevent hydration mismatches
 * between server (possibly UTC) and client (local timezone).
 */

const TIMEZONE = 'Europe/Berlin';

// Map short locale codes to Intl locale identifiers
const INTL_LOCALE_MAP: Record<string, string> = {
  de: 'de-DE',
  en: 'en-GB',
  fr: 'fr-FR',
  it: 'it-IT',
};

function toIntlLocale(locale: string): string {
  return INTL_LOCALE_MAP[locale] || INTL_LOCALE_MAP.de;
}

export interface EventDateTimeLabels {
  at?: string;
  oclock?: string;
}

/**
 * Format a date to long format (e.g. "1. Januar 2026" / "1 January 2026")
 */
function formatDateLong(date: Date, locale: string): string {
  return date.toLocaleDateString(toIntlLocale(locale), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: TIMEZONE,
  });
}

/**
 * Format a date to "18:00"
 */
function formatTimeShort(date: Date, locale: string): string {
  return date.toLocaleTimeString(toIntlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIMEZONE,
  });
}

/**
 * Check if two dates are on the same day
 */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Format ISO date string(s) to localized event date/time format.
 *
 * Examples (de): "1. Januar 2026 ab 18:00 Uhr"
 * Examples (en): "1 January 2026 from 18:00 o'clock"
 *
 * Labels ("ab"/"Uhr") can be customized via the labels parameter.
 */
export function formatEventDateTime(
  startIsoDate?: string | null,
  endIsoDate?: string | null,
  labels?: EventDateTimeLabels,
  locale = 'de'
): string {
  if (!startIsoDate) return '';

  const startDate = new Date(startIsoDate);
  if (isNaN(startDate.getTime())) {
    return startIsoDate;
  }

  const at = labels?.at ?? 'ab';
  const oclock = labels?.oclock ?? 'Uhr';

  const startDateStr = formatDateLong(startDate, locale);
  const startTimeStr = formatTimeShort(startDate, locale);

  // No end date - simple format
  if (!endIsoDate) {
    return `${startDateStr}, ${at} ${startTimeStr} ${oclock}`;
  }

  const endDate = new Date(endIsoDate);
  if (isNaN(endDate.getTime())) {
    return `${startDateStr}, ${at} ${startTimeStr} ${oclock}`;
  }

  const endTimeStr = formatTimeShort(endDate, locale);

  // Same day - compact format
  if (isSameDay(startDate, endDate)) {
    return `${startDateStr}, ${at} ${startTimeStr} - ${endTimeStr} ${oclock}`;
  }

  // Different days - full format for both
  const endDateStr = formatDateLong(endDate, locale);
  return `${startDateStr}, ${at} ${startTimeStr} ${oclock} - ${endDateStr}, ${at} ${endTimeStr} ${oclock}`;
}

/**
 * Format ISO date string to date only (e.g. "06.01.2026" / "06/01/2026")
 */
export function formatDate(isoDate: string, locale = 'de'): string {
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return isoDate;
  }
  return date.toLocaleDateString(toIntlLocale(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: TIMEZONE,
  });
}

/**
 * Format ISO date string to time only (HH:MM)
 */
export function formatTime(isoDate: string, locale = 'de'): string {
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return isoDate;
  }
  return date.toLocaleTimeString(toIntlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIMEZONE,
  });
}

/**
 * Format event date range based on whether dates are same day or different days.
 * The time (and its comma separator) is only included when the input actually
 * carries a time component (ISO strings containing a "T"); date-only inputs
 * render without a time.
 *
 * With time, same day: "06.01.2026, 14:21 - 16:00"
 * With time, different days: "06.01.2026, 14:21 - 07.01.2026, 14:21"
 * Date only, same day: "06.01.2026"
 * Date only, different days: "06.01.2026 - 07.01.2026"
 */
export function formatEventDateRange(
  startDateTime: string,
  endDateTime?: string,
  locale = 'de'
): string {
  const startDate = formatDate(startDateTime, locale);
  const hasStartTime = startDateTime.includes('T');
  const start = hasStartTime
    ? `${startDate}, ${formatTime(startDateTime, locale)}`
    : startDate;

  if (!endDateTime) {
    return start;
  }

  const endDate = formatDate(endDateTime, locale);
  const hasEndTime = endDateTime.includes('T');

  // Same day - only show end time (or nothing if no time info)
  if (startDate === endDate) {
    return hasEndTime ? `${start} - ${formatTime(endDateTime, locale)}` : start;
  }

  // Different days - show full date (and time if available) for both
  const end = hasEndTime
    ? `${endDate}, ${formatTime(endDateTime, locale)}`
    : endDate;
  return `${start} - ${end}`;
}
